import type { TFunction } from 'i18next'

import type { MenuItem } from './menu-item'

export const MIN_POPULAR_ITEMS = 6
export const MAX_POPULAR_ITEMS = 10

export type PopularItemRef = {
  itemReference: string
  rank: number
  orderCount?: number
  accountCount?: number | null
}

export type PopularItemStats = {
  orderCount: number
  accountCount: number
}

export type PopularItemsById = Map<string, PopularItemStats | null>

export const selectPopularItems = (
  ranked: PopularItemRef[],
  visibleItems: MenuItem[],
  { min = MIN_POPULAR_ITEMS, max = MAX_POPULAR_ITEMS }: { min?: number; max?: number } = {},
): MenuItem[] => {
  const itemsById = new Map(visibleItems.map((item) => [item._id, item]))

  const selected = [...ranked]
    .sort((a, b) => a.rank - b.rank)
    .map((ref) => itemsById.get(ref.itemReference))
    .filter((item): item is MenuItem => item !== undefined)
    .slice(0, max)

  return selected.length >= min ? selected : []
}

const toStats = (ref: PopularItemRef | undefined): PopularItemStats | null =>
  ref?.orderCount && ref.accountCount
    ? { orderCount: ref.orderCount, accountCount: ref.accountCount }
    : null

export const buildPopularItemsMap = (
  items: MenuItem[],
  ranked: PopularItemRef[],
): PopularItemsById => {
  const refsById = new Map(ranked.map((ref) => [ref.itemReference, ref]))

  return new Map(items.map((item) => [item._id, toStats(refsById.get(item._id))]))
}

export const formatPopularTooltip = (
  t: TFunction,
  stats: PopularItemStats | null,
): string | null =>
  stats
    ? t('marketplace:popularTooltip', {
        orderCount: stats.orderCount,
        accountCount: stats.accountCount,
        defaultValue:
          'Ordered {{orderCount}} time(s) by {{accountCount}} account(s) in the last 90 days.',
      })
    : null
